import type { I18n, MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { Plus, X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useController, useFormContext, useWatch, type UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { DateFormField, defineEntitySource, DocumentFormField, EntityPicker, EntityPickerField, EntitySheet, MaskedFormField, MoneyFormField, PercentFormField, SelectFormField, TextFormField, UFS } from '@/shared/form';
import { formatCnpj, isValidCnpj } from '@/shared/format/cnpj';
import { formatCpf, isValidCpf } from '@/shared/format/cpf';
import { bankCompeSource, lookupCep } from '@/shared/reference';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

import { listEmploymentStatus } from '../credit-note.api';
import { cleanDocument } from '../credit-note.list-actions';
import type { WarrantyItem } from './tab-form';
import type { WarrantyProductFacts } from './tab-warranty-facts';

// Drawer "Adicionar/Alterar Garantia" (pages/CreditNote/WarrantyTab/WarrantyForm): os 12 tipos com os
// campos, as regras (produto INSS, SIAPE/Zetra, Kovr) e as mensagens do `WarrantyFormSchema.tsx`. O que
// sai daqui é o item como o legado grava no formulário (cast do yup + `mapDataPrevFields`), sem fio: a
// garantia vai no Salvar da operação (contrato em `tab-form.tsx`).

/* eslint-disable lingui/no-unlocalized-strings -- valores técnicos do fio (U02), não texto de UI */
export const WARRANTY_FORM_TYPES = ['Vehicle', 'HomeEquity', 'JudiciaryProcess', 'Factoring', 'PublicPayroll', 'InvoiceFactoring', 'Consortium', 'DataprevEmployee', 'DataprevEmployeeRefinance', 'DataprevInss', 'Load', 'CarEquity'] as const;
type FormType = (typeof WARRANTY_FORM_TYPES)[number];
/* eslint-enable lingui/no-unlocalized-strings */

/** Rótulos do select "Tipo de garantia" (WarrantyForm.tsx:111-138), que não são os da lista (`mapTitleWarranty`). */
const TYPE_OPTION_LABELS: Readonly<Record<FormType, MessageDescriptor>> = {
  Vehicle: msg({ id: 'creditNote.warrantyForm.Vehicle', message: 'Veículo' }),
  HomeEquity: msg({ id: 'creditNote.warrantyForm.HomeEquity', message: 'Imóvel' }),
  JudiciaryProcess: msg({ id: 'creditNote.warrantyForm.JudiciaryProcess', message: 'Processo Judicial' }),
  Factoring: msg({ id: 'creditNote.warrantyForm.Factoring', message: 'Antecipação de Recebíveis' }),
  PublicPayroll: msg({ id: 'creditNote.warrantyForm.PublicPayroll', message: 'Consignado Público' }),
  InvoiceFactoring: msg({ id: 'creditNote.warrantyForm.InvoiceFactoring', message: 'Desconto' }),
  Consortium: msg({ id: 'creditNote.warrantyForm.Consortium', message: 'Consórcio' }),
  DataprevEmployee: msg({ id: 'creditNote.warrantyForm.DataprevEmployee', message: 'Crédito do Trabalhador' }),
  DataprevEmployeeRefinance: msg({ id: 'creditNote.warrantyForm.DataprevEmployeeRefinance', message: 'Crédito do Trabalhador - Refinanciamento' }),
  DataprevInss: msg({ id: 'creditNote.warrantyForm.DataprevInss', message: 'Consignado INSS' }),
  Load: msg({ id: 'creditNote.warrantyForm.Load', message: 'Carga' }),
  CarEquity: msg({ id: 'creditNote.warrantyForm.CarEquity', message: 'Car Equity' }),
};

/** As opções do tipo conforme o produto (WarrantyForm.tsx:111-138). */
export function warrantyTypeOptions(facts: WarrantyProductFacts): readonly FormType[] {
  if (facts.inss) return ['DataprevInss'];
  if (facts.office === 'zetra' || facts.office === 'siape') return ['PublicPayroll'];
  return WARRANTY_FORM_TYPES;
}

type Values = Record<string, unknown> & { warrantyType?: string | null | undefined };
type BankOption = Parameters<(typeof bankCompeSource)['toOption']>[0];

// ─── datas no formato do legado ─────────────────────────────────────────────

const pad = (value: number) => String(value).padStart(2, '0');

/** `toIsoStringWithTimezone` (DateFormat.ts:218-232): hora local com o `-03:00` fixo do legado. */
export function legacyIsoWithTimezone(date: Date): string {
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${String(date.getMilliseconds()).padStart(3, '0')}-03:00`;
}

/** A data escolhida no `DatePickerFormField` (meia-noite local, `toIsoStringWithTimezone`). */
const pickedDate = (civil: string) => `${civil}T00:00:00.000-03:00`;
/** O dia civil local de uma data do fio: `AAAA-MM-DD` fica; com hora sem fuso, é hora local; com fuso, converte. */
function civilOf(value: unknown): string | null {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
  if (value.length === 10 || !/(Z|[+-]\d{2}:?\d{2})$/.test(value)) return value.slice(0, 10);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value.slice(0, 10) : `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `getKovrWarrantyDeadline` (DateFormat.ts:215-216): hoje + 30 dias, na hora de agora. */
export function kovrWarrantyDeadline(now: Date): string {
  const next = new Date(now);
  next.setDate(next.getDate() + 30);
  return legacyIsoWithTimezone(next);
}

/** `new DefaultWarranty()` (generics.ts:65-83): o que o "Adicionar garantia" abre. */
export function defaultWarrantyValues(now: Date): Values {
  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  return {
    warrantyType: null,
    dueDate: now.toISOString(),
    emissionDate: now.toISOString(),
    previousAdeNumber: null,
    warrantyDeadline: legacyIsoWithTimezone(midnight),
    // eslint-disable-next-line lingui/no-unlocalized-strings -- valor técnico do fio (U02), não texto de UI
    warrantyOption: 'FreeMargin',
    dataprev_EmployeeAdmissionDate: now.toISOString(),
  };
}

// Campos de data que o drawer edita como dia civil e o item guarda no formato do legado.
const DATE_FIELDS = ['dueDate', 'emissionDate', 'dataprev_EmployeeAdmissionDate', 'warrantyDeadline'] as const;
const MONEY_AS_TEXT = ['invoiceValue', 'discountValue', 'presentValue'] as const;

/** Item salvo → valores do drawer: datas como dia civil, dinheiro-texto como número, sim/não como texto. */
export function warrantyFormValues(item: WarrantyItem): Values {
  const values: Values = { ...item };
  for (const field of DATE_FIELDS) {
    const civil = civilOf(values[field]);
    if (civil) values[`${field}__civil`] = civil;
  }
  // WarrantFormContainer.tsx:47-58: Desconto abre com os valores em número.
  if (item.warrantyType?.toLowerCase() === 'invoicefactoring') {
    for (const field of [...MONEY_AS_TEXT, 'paymentAmountInCents'] as const) values[field] = item[field] === null || item[field] === undefined ? null : Number(item[field]);
  }
  if (typeof item['constituted'] === 'boolean') values['constituted'] = String(item['constituted']);
  if (typeof item['dataprev_DiscountStartPeriod'] === 'string') values['dataprev_DiscountStartPeriod'] = discountDisplay(item['dataprev_DiscountStartPeriod']);
  return values;
}

/** `AAAAMM` → `MM/AAAA` (a `replace` do campo, WarrantyForm.tsx:84-87); o resto fica como está. */
function discountDisplay(value: string): string {
  const match = /^(\d{4})(\d{2})$/.exec(value);
  return match ? `${match[2] ?? ''}/${match[1] ?? ''}` : value;
}

/** Texto de um valor do formulário (número ou texto); o resto vira vazio. */
const str = (value: unknown): string => (typeof value === 'string' ? value : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '');

const numberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const number = typeof value === 'number' ? value : Number(str(value).replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};
const stringOrNull = (value: unknown): string | null => (value === null || value === undefined ? null : str(value));

interface ToItemOptions {
  readonly facts: WarrantyProductFacts;
  readonly edit: boolean;
  readonly original: WarrantyItem | undefined;
  readonly kovr: boolean;
  readonly now: Date;
  readonly autofilledInssCpf: string | null;
  readonly alphanumeric: boolean;
}

/**
 * Valores do drawer → item do formulário, como o `onSubmit` do legado (WarrantFormContainer.tsx:64-104):
 * o cast do yup de cada tipo, `mapDataPrevFields`, o consórcio e a carga com o CNPJ limpo, o dia do repasse
 * só no Zetra e o prazo Kovr.
 */
export function warrantyItemFromForm(values: Values, options: ToItemOptions): WarrantyItem {
  const type = values.warrantyType ?? '';
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) if (!key.endsWith('__civil')) out[key] = value;
  // Datas: o dia que a pessoa escolheu vira a meia-noite local do seletor; o que não mudou fica como veio.
  for (const field of DATE_FIELDS) {
    const civil = values[`${field}__civil`];
    if (typeof civil !== 'string') continue;
    const before = options.original ? civilOf(options.original[field]) : civilOf(values[field]);
    if (civil !== before || !(field in out)) out[field] = pickedDate(civil);
  }
  // Documento: só os separadores, nunca `\D` (o CNPJ alfanumérico mantém as letras).
  const clean = (value: unknown) => (typeof value === 'string' ? cleanDocument(value) : value);

  if (type === 'Factoring') {
    out['totalValue'] = numberOrNull(values['totalValue']);
    if (typeof values['constituted'] === 'string') out['constituted'] = values['constituted'] === 'true';
  }
  if (type === 'Vehicle' || type === 'CarEquity') {
    for (const field of ['yearOfManufacture', 'yearOfModel'] as const) out[field] = stringOrNull(values[field]);
  }
  if (type === 'DataprevEmployee' || type === 'DataprevEmployeeRefinance') {
    // `string()` do yup e depois o `mapDataPrevFields` (payrollAgreement/helpers/index.ts:78-92).
    const employer = values['dataprev_EmployerRegistrationNumber'];
    const period = typeof values['dataprev_DiscountStartPeriod'] === 'string' ? values['dataprev_DiscountStartPeriod'] : '';
    out['dataprev_EmployerRegistrationCode'] = 1;
    out['dataprev_EmployerRegistrationNumber'] = employer ? clean(str(employer)) : null;
    out['dataprev_DiscountStartPeriod'] = period ? period.split('/').reverse().join('') : null;
    out['dataprev_EmployeeAdmissionDate'] = out['dataprev_EmployeeAdmissionDate'] ? out['dataprev_EmployeeAdmissionDate'] : null;
    out['totalValue'] = values['totalValue'] ? Number(values['totalValue']) : null;
  }
  if (type === 'DataprevInss') {
    for (const field of ['inss_AvailableMargin', 'inss_MarginValue', 'inss_MaximumInstallments'] as const) out[field] = numberOrNull(values[field]);
    // A máscara do campo fica no valor digitado (react-text-mask); o preenchido pelo tomador vai limpo.
    const cpf = typeof values['inss_RegistrationNumber'] === 'string' ? values['inss_RegistrationNumber'] : '';
    if (cpf && cpf !== options.autofilledInssCpf) out['inss_RegistrationNumber'] = formatCpf(cpf) ?? cpf;
  }
  if (type === 'HomeEquity') {
    out['width'] = numberOrNull(values['width']);
    out['length'] = numberOrNull(values['length']);
  }
  if (type === 'JudiciaryProcess') out['valueOfCause'] = numberOrNull(values['valueOfCause']);
  if (type === 'InvoiceFactoring') {
    // `string()` nos valores e no pagamento (WarrantyFormSchema.tsx:452-470): centavos em texto.
    for (const field of [...MONEY_AS_TEXT, 'paymentAmountInCents'] as const) out[field] = stringOrNull(values[field]);
    for (const field of ['paymentItemIndex', 'paymentItemTotal', 'storeCode', 'productCode', 'index', 'payerZipCodeAdress'] as const) if (field in values) out[field] = numberOrNull(values[field]);
    if ('yourNumber' in values) out['yourNumber'] = stringOrNull(values['yourNumber']) || null;
  }
  if (type === 'PublicPayroll') {
    out['paymentAmountInCents'] = numberOrNull(values['paymentAmountInCents']);
    // `date().nullable()` do `publicPayrollSchema`: o Date do yup vira ISO UTC no JSON.
    const deadline = out['warrantyDeadline'];
    if (typeof deadline === 'string' && deadline !== '') out['warrantyDeadline'] = new Date(deadline).toISOString();
    if (options.kovr && options.edit) out['warrantyDeadline'] = kovrWarrantyDeadline(options.now);
    const paying = values['payingSourceRegistrationNumber'];
    if (typeof paying === 'string' && paying !== '') out['payingSourceRegistrationNumber'] = formatCnpj(paying) ?? paying;
  }
  if (type === 'Consortium') {
    const admin = values['administratorRegistrationNumber'];
    out['administratorRegistrationNumber'] = typeof admin === 'string' && admin !== '' ? clean(admin) : null;
    out['administratorName'] = values['administratorName'] ?? null;
  }
  if (options.facts.office !== 'zetra') delete out['paymentTransferDay'];
  if (str(values['insuranceCompanyCNPJ']).length > 0) out['insuranceCompanyCNPJ'] = clean(values['insuranceCompanyCNPJ']);
  return out;
}

// ─── validação (WarrantyFormSchema.tsx) ─────────────────────────────────────

const NEED = (label: string) => msg`${label}: precisa ser preenchido.`;

function warrantySchema(i18n: I18n, facts: WarrantyProductFacts): z.ZodType<Values, Values> {
  const siape = facts.office === 'siape';
  const need = (label: MessageDescriptor) => i18n._(NEED(i18n._(label)));
  const filled = (value: unknown) => str(value).trim() !== '' || (Array.isArray(value) && value.length > 0);
  const doc = (value: unknown) => {
    const raw = typeof value === 'string' ? cleanDocument(value).toUpperCase() : '';
    if (raw.length === 14) return isValidCnpj(raw);
    if (raw.length === 11) return isValidCpf(raw);
    return false;
  };
  const schema: z.ZodType<Values, Values> = z.looseObject({ warrantyType: z.string().nullish() }).superRefine((values: Values, ctx) => {
    const issue = (path: string, message: string) => ctx.addIssue({ code: 'custom', path: [path], message });
    const required = (path: string, label: MessageDescriptor) => {
      if (!filled(values[path])) issue(path, need(label));
    };
    const document = (path: string, message: MessageDescriptor) => {
      if (filled(values[path]) && !doc(values[path])) issue(path, i18n._(message));
    };
    const type = values.warrantyType;
    if (!type) {
      issue('warrantyType', need(msg`Tipo de garantia`));
      return;
    }
    if (!(WARRANTY_FORM_TYPES as readonly string[]).includes(type)) {
      issue('warrantyType', i18n._(msg`Selecione um tipo de garantia`));
      return;
    }
    const date = (field: string, label: MessageDescriptor) => {
      if (!filled(values[`${field}__civil`]) && !filled(values[field])) issue(`${field}__civil`, need(label));
    };
    const year = (field: string, label: MessageDescriptor, short: MessageDescriptor) => {
      required(field, label);
      if (filled(values[field]) && String(values[field]).length !== 4) issue(field, i18n._(short));
    };
    switch (type) {
      case 'Factoring':
        if (values['splitMethodType'] === 'Absolute' || values['splitMethodType'] === 'Percentage') required('totalValue', msg`Valor total`);
        required('type', msg`Tipo de garantia`);
        required('splitMethodType', msg`Tipo de divisão`);
        required('constituted', msg`Recebível Performado`);
        break;
      case 'Vehicle':
      case 'CarEquity':
        required('renavam', msg`Renavam`);
        required('board', msg`Placa`);
        required('typeOfVehicle', msg`Tipo`);
        required('chassis', msg`Chassi`);
        required('brand', msg`Marca`);
        required('color', msg`Cor`);
        required('model', msg`Modelo`);
        year('yearOfManufacture', msg`Ano de fabricação`, msg`Ano de fabricação precisa conter 4 dígitos.`);
        year('yearOfModel', msg`Ano do modelo`, msg`Ano do modelo precisa conter 4 dígitos.`);
        required('typeOfFuel', msg`Tipo de combustível`);
        required('plateState', msg`UF da placa`);
        required('operationState', msg`UF da operação`);
        if (type === 'Vehicle') {
          required('supplier', msg`Fornecedor`);
          required('taxNumberOfSupplier', msg`CNPJ do fornecedor`);
          document('taxNumberOfSupplier', msg`CNPJ inválido.`);
          required('sellerRegistrationNumber', msg`CNPJ do vendedor`);
          document('sellerRegistrationNumber', msg`CNPJ inválido.`);
        }
        break;
      case 'DataprevEmployee':
      case 'DataprevEmployeeRefinance': {
        required('dataprev_EmployeeCode', msg`Matrícula do Trabalhador`);
        required('dataprev_EmployerRegistrationNumber', msg`Número inscrição do Empregador`);
        document('dataprev_EmployerRegistrationNumber', msg`CNPJ do Empregador inválido.`);
        const period = str(values['dataprev_DiscountStartPeriod']).replace(/\s+/g, '').replace(/\//g, '');
        if (!filled(values['dataprev_DiscountStartPeriod'])) issue('dataprev_DiscountStartPeriod', need(msg`Competência início desconto`));
        else if (period.length < 6) issue('dataprev_DiscountStartPeriod', i18n._(msg`Competência início desconto inválida. (MM/AAAA)`));
        required('totalValue', msg`Valor margem`);
        if (type === 'DataprevEmployeeRefinance' && !(Array.isArray(values['refinancedContractNumbers']) && values['refinancedContractNumbers'].length > 0)) {
          issue('refinancedContractNumbers', i18n._(msg`Adicione pelo menos um contrato para quitar.`));
        }
        break;
      }
      case 'DataprevInss':
        required('inss_RegistrationNumber', msg`CPF do beneficiário`);
        document('inss_RegistrationNumber', msg`CPF do beneficiário inválido.`);
        required('inss_BenefitNumber', msg`Número do benefício`);
        required('inss_Name', msg`Nome do beneficiário`);
        required('inss_BenefitType', msg`Espécie do benefício`);
        required('inss_BenefitTypeDescription', msg`Descrição da espécie`);
        required('inss_AvailableMargin', msg`Margem disponível`);
        required('inss_MarginValue', msg`Margem utilizada`);
        required('inss_MaximumInstallments', msg`Prazo máximo`);
        if (filled(values['inss_MaximumInstallments']) && numberOrNull(values['inss_MaximumInstallments']) === null) issue('inss_MaximumInstallments', i18n._(msg`Prazo máximo inválido.`));
        break;
      case 'HomeEquity':
        required('registryOffice', msg`Cartório de registro`);
        required('registrationNumber', msg`Matrícula`);
        required('width', msg`Largura em metros`);
        if (filled(values['width']) && numberOrNull(values['width']) === null) issue('width', need(msg`Largura em metros`));
        required('length', msg`Comprimento em metros`);
        if (filled(values['length']) && numberOrNull(values['length']) === null) issue('length', need(msg`Comprimento em metros`));
        required('addressName', msg`Endereço`);
        required('zipCodeAddress', msg`CEP`);
        required('uf', msg`UF`);
        required('district', msg`Bairro`);
        required('addressNumber', msg`Número`);
        required('complementAddress', msg`Complemento`);
        break;
      case 'JudiciaryProcess':
        required('number', msg`Nº do processo`);
        required('court', msg`Vara / Tribunal`);
        required('valueOfCause', msg`Valor da causa`);
        required('claimant', msg`Requerente / Autor`);
        required('claimed', msg`Requerido / Réu`);
        required('typeOfProcess', msg`Tipo do processo`);
        break;
      case 'PublicPayroll':
        required('employeeCode', msg`Nº da matrícula`);
        if (siape) required('agreementCode', msg`Código externo do convênio`);
        required('governmentDepartmentCode', msg`Código do órgão`);
        required('paymentAmountInCents', msg`Valor da margem`);
        if (siape && !filled(values['employmentStatusDescription'])) issue('employmentStatusDescription', i18n._(msg`Selecione a situação vínculo funcional.`));
        document('payingSourceRegistrationNumber', msg`CNPJ inválido.`);
        // eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio (U02), não texto de UI
        if (!['None', 'FreeMargin', 'Portability'].includes(str(values['warrantyOption']))) issue('warrantyOption', i18n._(msg`Selecione a opção de garantia.`));
        if (values['warrantyOption'] === 'Portability') required('previousAdeNumber', msg`Número do ADE anterior`);
        break;
      case 'InvoiceFactoring':
        required('payerName', msg`Nome do pagador`);
        required('payerRegistrationNumber', msg`CPF/CNPJ`);
        document('payerRegistrationNumber', msg`CPF/CNPJ: inválido`);
        required('contractNumber', msg`Número do contrato`);
        required('invoiceValue', msg`Valor nominal`);
        required('invoiceNumber', msg`Número nominal`);
        required('discountValue', msg`Valor desconto`);
        required('presentValue', msg`Valor presente`);
        date('dueDate', msg`Data de vencimento`);
        required('paymentAmountInCents', msg`Valor do pagamento`);
        break;
      case 'Consortium':
        document('administratorRegistrationNumber', msg`CNPJ: inválido`);
        required('group', msg`Grupo`);
        required('quota', msg`Cota`);
        break;
      case 'Load':
        required('idInvoice', msg`Número da fatura`);
        required('blNumber', msg`Número de conhecimento de Embarque`);
        required('ncm', msg`NCM`);
        required('valueBRL', msg`Valor BRL`);
        required('policyNumber', msg`Número da Apólice`);
        required('insuranceCompanyName', msg`Nome da Seguradora`);
        required('insuranceCompanyCNPJ', msg`CNPJ da seguradora`);
        document('insuranceCompanyCNPJ', msg`CNPJ da seguradora inválido.`);
        break;
      default:
        break;
    }
  });
  return schema;
}

// ─── campos compostos ───────────────────────────────────────────────────────

/** Lista de textos: `TagField` (arranjos de pagamento) e `ItemListManager` (contratos a quitar). */
function TextListField({ name, label, inputLabel, addLabel, empty, itemsLabel, maxLength, required }: { readonly name: string; readonly label: string; readonly inputLabel: string; readonly addLabel: string; readonly empty: string; readonly itemsLabel: string; readonly maxLength?: number; readonly required?: boolean }) {
  const { t } = useLingui();
  const id = useId();
  const { control } = useFormContext<Values>();
  const { field, fieldState } = useController({ name, control });
  const [draft, setDraft] = useState('');
  const items: string[] = Array.isArray(field.value) ? (field.value as unknown[]).map(String) : [];
  const add = () => {
    const value = draft.trim();
    if (!value || items.includes(value)) return;
    field.onChange([...items, value]);
    setDraft('');
  };
  const error = fieldState.error?.message;
  return (
    <fieldset className="flex min-w-0 flex-col gap-2" aria-describedby={error ? `${id}-error` : undefined}>
      <legend className="mb-1.5 text-sm font-medium text-foreground">
        {label}
        {required ? (
          <>
            <span aria-hidden className="ml-0.5 text-err">
              *
            </span>
            <span className="sr-only">{t`(obrigatório)`}</span>
          </>
        ) : null}
      </legend>
      <div className="flex gap-2">
        <Input
          aria-label={inputLabel}
          placeholder={inputLabel}
          value={draft}
          {...(maxLength ? { maxLength } : {})}
          {...(error ? { 'aria-invalid': true } : {})}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="secondary" onClick={add}>
          <Plus aria-hidden />
          {addLabel}
        </Button>
      </div>
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-sm text-err">
          {error}
        </p>
      ) : null}
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul aria-label={itemsLabel} className="flex flex-wrap gap-2">
          {items.map((item) => (
            <li key={item} className="flex items-center gap-1 rounded-full border border-border bg-muted px-3 py-1 text-sm">
              {item}
              <Button type="button" variant="ghost" size="icon" className="size-6" aria-label={t`Remover ${item}`} onClick={() => field.onChange(items.filter((other) => other !== item))}>
                <X aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}

/** "Competência início desconto" com a máscara `MM/AAAA` (`monthYearMask`). */
function MonthYearField({ name, label, required }: { readonly name: string; readonly label: string; readonly required?: boolean }) {
  const { t } = useLingui();
  const id = useId();
  const { control } = useFormContext<Values>();
  const { field, fieldState } = useController({ name, control });
  const value = typeof field.value === 'string' ? field.value : '';
  const error = fieldState.error?.message;
  return (
    <div data-slot="form-field" className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
        {required ? (
          <>
            <span aria-hidden className="ml-0.5 text-err">
              *
            </span>
            <span className="sr-only">{t`(obrigatório)`}</span>
          </>
        ) : null}
      </label>
      <Input
        id={id}
        inputMode="numeric"
        placeholder={t`MM/AAAA`}
        value={value}
        {...(error ? { 'aria-invalid': true, 'aria-describedby': `${id}-error` } : {})}
        onBlur={field.onBlur}
        onChange={(event) => {
          const digits = event.target.value.replace(/\D/g, '').slice(0, 6);
          field.onChange(digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits);
        }}
      />
      {error ? (
        <p id={`${id}-error`} className="text-sm text-err">
          {error}
        </p>
      ) : null}
    </div>
  );
}

type EmploymentStatus = Awaited<ReturnType<typeof listEmploymentStatus>>['data'][number];

/** `useGetEmploymentStatus` (employmentStatusContext.tsx:14-75): `GET /EmploymentStatus`, rótulo "nome - Código: código". */
const employmentStatusSource = defineEntitySource<EmploymentStatus>({
  key: ['credit-note', 'source', 'employment-status'],
  search: async (query, signal) => (await listEmploymentStatus(query || undefined, signal)).data,
  toOption: (item, i18n) => {
    const name = item.name ?? '';
    const code = String(item.code);
    return { value: code, label: i18n._(msg`${name} - Código: ${code}`) };
  },
});

/** Situação do vínculo funcional (SIAPE): o autocomplete do legado grava descrição e código (WarrantyForm.tsx:906-922). */
export function EmploymentStatusField() {
  const { t } = useLingui();
  const id = useId();
  const { control, setValue } = useFormContext<Values>();
  const { field, fieldState } = useController({ name: 'employmentStatusDescription', control });
  const code = useWatch({ control, name: 'employmentStatusCode' });
  const error = fieldState.error?.message;
  return (
    <div data-slot="form-field" className="flex min-w-0 flex-col gap-1.5">
      <span id={id} className="text-sm font-medium text-foreground">
        {t`Descrição situação vínculo funcional`}
        <span aria-hidden className="ml-0.5 text-err">
          *
        </span>
        <span className="sr-only">{t`(obrigatório)`}</span>
      </span>
      <EntityPicker
        source={employmentStatusSource}
        value={typeof code === 'string' || typeof code === 'number' ? String(code) : null}
        selectedLabel={typeof field.value === 'string' ? field.value : null}
        control={{ 'aria-labelledby': id, ...(error ? { 'aria-invalid': true } : {}) }}
        onChange={(value, _option, item) => {
          setValue('employmentStatusCode', value, { shouldDirty: true });
          field.onChange(item?.name ?? null);
        }}
      />
      {error ? <p className="text-sm text-err">{error}</p> : null}
    </div>
  );
}

/** "Inspeção Veicular" (WarrantyForm.tsx:517-580): só leitura, com o grupo e a descrição do status. */
function VehicleInspection() {
  const { i18n, t } = useLingui();
  const { control } = useFormContext<Values>();
  const [id, display, raw, updated] = useWatch({ control, name: ['inspectionId', 'inspectionStatusDisplay', 'inspectionStatus', 'inspectionUpdatedAt'] });
  const status = typeof display === 'string' && display ? display : typeof raw === 'string' ? raw : '';
  const info = inspectionInfo(status);
  const date = civilOf(updated);
  return (
    <section aria-label={t`Inspeção Veicular`} className="flex flex-col gap-3 border-t border-border pt-4">
      <h3 className="text-base font-semibold text-foreground">{t`Inspeção Veicular`}</h3>
      <dl className="grid gap-3 text-sm md:grid-cols-3">
        <div>
          <dt className="text-muted-foreground">{t`Processo Vistoria Veicular`}</dt>
          <dd className="text-foreground">{typeof id === 'string' && id ? id : t`N/D`}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t`Status Vistoria Veicular`}</dt>
          <dd className="text-foreground">{status || t`N/D`}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t`Últ. Atualização Vistoria Veicular`}</dt>
          <dd className="text-foreground">{date ? new Date(`${date}T12:00:00`).toLocaleDateString(i18n.locale) : t`N/D`}</dd>
        </div>
      </dl>
      {status ? (
        <div className="rounded-md border border-border bg-muted px-4 py-3">
          <p className="text-xs font-semibold text-muted-foreground">{i18n._(info.group)}</p>
          <p className="text-sm text-foreground">{i18n._(info.description)}</p>
        </div>
      ) : null}
    </section>
  );
}

/** `getInspectionStatusInfo` (inspectionStatus.ts:20-153): grupo e descrição pelo rótulo do AutoVist. */
const INSPECTION_INFO: Readonly<Record<string, { readonly group: MessageDescriptor; readonly description: MessageDescriptor }>> = {
  'o envio realizado de forma incompleta': { group: msg`Parcialmente recebido`, description: msg`Ainda faltam informações ou imagens.` },
  'aguardando análise de risco': { group: msg`Parcialmente recebido`, description: msg`As imagens foram recebidas com sucesso e o processo está em análise.` },
  'aguardando fotos adicionais': { group: msg`Parcialmente recebido`, description: msg`Algumas imagens não atenderam aos critérios e foi solicitado o envio de novas fotos.` },
  aprovado: { group: msg`Aprovado/Concluído`, description: msg`Processo finalizado com aprovação.` },
  concluído: { group: msg`Aprovado/Concluído`, description: msg`Processo finalizado com aprovação.` },
  'sujeito à análise - concluído': { group: msg`Sujeito à análise - Concluído`, description: msg`Processo finalizado, porém ainda depende das regras internas da companhia para a decisão final.` },
  'aguardando foto até 06 horas': { group: msg`Aguardando fotos`, description: msg`Status inicial após a abertura do link. Indica que o cliente ainda não enviou as imagens.` },
  'aguardando foto até 12 horas': { group: msg`Aguardando fotos`, description: msg`Continuação do status anterior. Indica que o cliente ainda não realizou o envio das imagens dentro do prazo.` },
  'aguardando processamento das solicitações': { group: msg`Aguardando fotos`, description: msg`As imagens já foram enviadas e o sistema está processando o upload das informações.` },
  'recusado - concluído': { group: msg`Recusado - Concluído`, description: msg`Processo finalizado com reprovação.` },
};
const INSPECTION_DEFAULT = { group: msg`Status indisponível`, description: msg`Sem informações detalhadas sobre o status atual da vistoria.` };
function inspectionInfo(status: string) {
  return INSPECTION_INFO[status.trim().toLowerCase()] ?? INSPECTION_DEFAULT;
}

// ─── o formulário por tipo ──────────────────────────────────────────────────

const GRID = 'grid gap-4 md:grid-cols-2';

interface FieldsProps {
  readonly form: UseFormReturn<Values>;
  readonly facts: WarrantyProductFacts;
  readonly options: readonly FormType[];
  readonly edit: boolean;
  readonly kovr: boolean;
  readonly now: Date;
  readonly borrower: { readonly name: string | null; readonly document: string | null };
}

function WarrantyFields({ form, facts, options, edit, kovr, now, borrower }: FieldsProps) {
  const { i18n, t } = useLingui();
  const { control, setValue, getValues } = form;
  const type = useWatch({ control, name: 'warrantyType' });
  const split = useWatch({ control, name: 'splitMethodType' });
  const option = useWatch({ control, name: 'warrantyOption' });
  const status = useWatch({ control, name: 'employmentStatusDescription' });
  const deadline = useWatch({ control, name: 'warrantyDeadline' });
  const zetra = facts.office === 'zetra';
  const siape = facts.office === 'siape';
  const publicPayroll = type === 'PublicPayroll';
  const ufOptions = UFS.map((uf) => ({ value: uf, label: uf }));
  const yesNo = [
    { value: 'true', label: t`Sim` },
    { value: 'false', label: t`Não` },
  ];

  // WarrantyForm.tsx:103-109: no INSS, nome e CPF do tomador entram se estiverem vazios.
  useEffect(() => {
    if (type !== 'DataprevInss') return;
    if (borrower.name && !getValues('inss_Name')) setValue('inss_Name', borrower.name);
    if (borrower.document && !getValues('inss_RegistrationNumber')) setValue('inss_RegistrationNumber', borrower.document.replace(/[^A-Za-z0-9]/g, ''));
  }, [type, borrower.name, borrower.document, getValues, setValue]);

  // PublicPayrollForm.tsx:21-25: no Kovr, a garantia nova já nasce com o prazo de 30 dias.
  useEffect(() => {
    if (kovr && !edit && publicPayroll) {
      const next = kovrWarrantyDeadline(now);
      setValue('warrantyDeadline', next);
      setValue('warrantyDeadline__civil', next.slice(0, 10));
    }
  }, [kovr, edit, publicPayroll, now, setValue]);

  const kovrWarning = kovr && edit && zetra && publicPayroll && typeof deadline === 'string' && civilOf(deadline) !== kovrWarrantyDeadline(now).slice(0, 10);
  const fillAddress = async (zip: string) => {
    if (zip.length !== 8) return;
    try {
      const found = await lookupCep(zip);
      if (!found) return;
      setValue('addressName', found.addressName, { shouldDirty: true });
      setValue('district', found.district, { shouldDirty: true });
      setValue('city', found.city, { shouldDirty: true });
      setValue('uf', found.uf, { shouldDirty: true });
    } catch {
      // ViaCEP fora do ar: fica o que já estava escrito (shared/form, P14).
    }
  };

  return (
    <>
      <SelectFormField<Values> name="warrantyType" label={t`Tipo de garantia`} required options={options.map((value) => ({ value, label: i18n._(TYPE_OPTION_LABELS[value]) }))} placeholder={t`Selecione`} />

      {type === 'DataprevEmployee' || type === 'DataprevEmployeeRefinance' ? (
        <div className={GRID}>
          <DocumentFormField<Values> name="dataprev_EmployerRegistrationNumber" label={t`CNPJ do Empregador`} legalPerson required />
          <TextFormField<Values> name="dataprev_EmployerName" label={t`Nome do Empregador`} required />
          <TextFormField<Values> name="dataprev_EmployeeCode" label={t`Matrícula do Trabalhador`} required />
          <DateFormField<Values> name="dataprev_EmployeeAdmissionDate__civil" label={t`Data de admissão`} required />
          <MonthYearField name="dataprev_DiscountStartPeriod" label={t`Competência início desconto`} required />
          <MoneyFormField<Values> name="totalValue" label={t`Valor margem`} required />
          {type === 'DataprevEmployeeRefinance' ? (
            <div className="md:col-span-2">
              <TextListField name="refinancedContractNumbers" label={t`Lista de contratos a quitar`} inputLabel={t`Digite o número do contrato`} addLabel={t`Adicionar`} empty={t`Nenhum contrato adicionado`} itemsLabel={t`Contratos adicionados`} maxLength={15} />
            </div>
          ) : null}
        </div>
      ) : null}

      {type === 'DataprevInss' ? (
        <div className={GRID}>
          <TextFormField<Values> name="inss_Name" label={t`Nome`} required />
          <MaskedFormField<Values> name="inss_RegistrationNumber" label={t`CPF`} kind="cpf" required />
          <TextFormField<Values> name="inss_BenefitNumber" label={t`Número do benefício`} required />
          <TextFormField<Values> name="inss_BenefitType" label={t`Espécie do benefício`} required />
          <TextFormField<Values> name="inss_BenefitTypeDescription" label={t`Descrição da espécie`} required />
          <TextFormField<Values> name="inss_ProposalNumber" label={t`Número da proposta`} />
          <TextFormField<Values> name="inss_PaymentAccountType" label={t`Tipo da conta bancária`} />
          <TextFormField<Values> name="inss_PaymentAccountTypeDescription" label={t`Descrição do tipo da conta`} />
          {/* AutocompleteField com `displayName` igual ao `name` (WarrantyForm.tsx:272-279): o rótulo do banco fica no campo. */}
          <EntityPickerField<Values, BankOption> name="inss_PaymentBankCode" displayName="inss_PaymentBankCode" label={t`Banco`} source={bankCompeSource} />
          <TextFormField<Values> name="inss_PaymentBankAgency" label={t`Agência bancária`} />
          <TextFormField<Values> name="inss_PaymentBankAccountNumber" label={t`Número da conta bancária`} />
          <InssMarginField />
          <MoneyFormField<Values> name="inss_MarginValue" label={t`Margem averbação`} required />
          <TextFormField<Values> name="inss_MaximumInstallments" label={t`Prazo máximo (parcelas)`} inputMode="numeric" required />
        </div>
      ) : null}

      {type === 'Factoring' ? (
        <div className={GRID}>
          <TextFormField<Values> name="register" label={t`Registradora`} />
          <SelectFormField<Values>
            name="type"
            label={t`Tipo de contrato`}
            required
            options={[
              // eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio (U02), não texto de UI
              { value: 'Collateral', label: t`Garantia` },
              // eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio (U02), não texto de UI
              { value: 'OwnershipChange', label: t`Troca de Titularidade` },
            ]}
          />
          <SelectFormField<Values>
            name="splitMethodType"
            label={t`Modelo de Divisão`}
            required
            options={[
              // eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio (U02), não texto de UI
              { value: 'Absolute', label: t`Valor absoluto` },
              // eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio (U02), não texto de UI
              { value: 'Percentage', label: t`Percentual` },
            ]}
          />
          {split === 'Absolute' ? <MoneyFormField<Values> name="totalValue" label={t`Valor total`} required /> : null}
          {split === 'Percentage' ? <PercentFormField<Values> name="totalValue" label={t`Valor total (%)`} required /> : null}
          <SelectFormField<Values> name="constituted" label={t`Recebível Performado`} required options={yesNo} />
          <div className="md:col-span-2">
            <TextListField name="paymentNetworks" label={t`Arranjos de pagamento`} inputLabel={t`Adicionar arranjos de pagamento`} addLabel={t`Adicionar`} empty={t`Nenhum arranjo adicionado`} itemsLabel={t`Arranjos adicionados`} />
          </div>
        </div>
      ) : null}

      {type === 'Vehicle' || type === 'CarEquity' ? (
        <>
          <div className={GRID}>
            <TextFormField<Values> name="renavam" label={t`Renavam`} required />
            <TextFormField<Values> name="typeOfVehicle" label={t`Tipo de veículo`} required />
            <TextFormField<Values> name="chassis" label={t`Chassi`} required />
            <TextFormField<Values> name="board" label={t`Placa`} required />
            <TextFormField<Values> name="brand" label={t`Marca`} required />
            <TextFormField<Values> name="color" label={t`Cor`} required />
            <TextFormField<Values> name="model" label={t`Modelo`} required />
            <TextFormField<Values> name="yearOfModel" label={t`Ano do modelo`} inputMode="numeric" maxLength={4} required />
            <TextFormField<Values> name="yearOfManufacture" label={t`Ano de fabricação`} inputMode="numeric" maxLength={4} required />
            <TextFormField<Values> name="typeOfFuel" label={t`Tipo de combustível`} required />
            <SelectFormField<Values> name="plateState" label={t`UF Placa`} required options={ufOptions} />
            <SelectFormField<Values> name="operationState" label={t`UF Operação`} required options={ufOptions} />
          </div>
          <VehicleInspection />
          {type === 'Vehicle' ? (
            <div className={GRID}>
              <TextFormField<Values> name="supplier" label={t`Fornecedor`} required />
              <DocumentFormField<Values> name="taxNumberOfSupplier" label={t`CNPJ do Fornecedor`} legalPerson required />
              <TextFormField<Values> name="sellerCompanyName" label={t`Revenda`} />
              <DocumentFormField<Values> name="sellerRegistrationNumber" label={t`CNPJ de quem efetuou a venda`} legalPerson required />
              <MoneyFormField<Values> name="downPayment" label={t`Valor de entrada`} />
              <MoneyFormField<Values> name="valueInCash" label={t`Valor do veículo à vista`} />
              <MoneyFormField<Values> name="subtotalAmount" label={t`Subtotal (Veículo + acessórios + outras despesas incluídas na operação a pedido do consumidor)`} />
              <MoneyFormField<Values> name="totalAmountFinancedTaxFree" label={t`Valor total a ser financiado sem impostos`} />
            </div>
          ) : null}
        </>
      ) : null}

      {type === 'HomeEquity' ? (
        <div className={GRID}>
          <TextFormField<Values> name="registryOffice" label={t`Cartório de Registro`} required />
          <TextFormField<Values> name="registrationNumber" label={t`Matrícula`} required />
          <TextFormField<Values> name="width" label={t`Largura (em metros)`} inputMode="decimal" required />
          <TextFormField<Values> name="length" label={t`Comprimento (em metros)`} inputMode="decimal" required />
          <ZipField onComplete={(zip) => void fillAddress(zip)} />
          <TextFormField<Values> name="addressName" label={t`Endereço`} required />
          <TextFormField<Values> name="addressNumber" label={t`Número`} required />
          <TextFormField<Values> name="complementAddress" label={t`Complemento`} required />
          <SelectFormField<Values> name="uf" label={t`UF`} required options={ufOptions} />
          <TextFormField<Values> name="district" label={t`Bairro`} required />
          <TextFormField<Values> name="propertyFeatures" label={t`Características do imóvel`} />
        </div>
      ) : null}

      {type === 'JudiciaryProcess' ? (
        <div className={GRID}>
          <TextFormField<Values> name="number" label={t`Nº do processo`} required />
          <TextFormField<Values> name="court" label={t`Vara / Tribunal`} required />
          <MoneyFormField<Values> name="valueOfCause" label={t`Valor da causa`} required />
          <TextFormField<Values> name="claimant" label={t`Requerente / Autor`} required />
          <TextFormField<Values> name="claimed" label={t`Requerido / Réu`} required />
          <TextFormField<Values> name="typeOfProcess" label={t`Tipo de processo`} required />
        </div>
      ) : null}

      {publicPayroll ? (
        <div className={GRID}>
          {/* O Zetra desenhava "Opção de operação" duas vezes (PublicPayrollForm + o bloco do consignado): aqui, uma. */}
          <SelectFormField<Values>
            name="warrantyOption"
            label={t`Opção de operação`}
            required
            options={[
              // eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio (U02), não texto de UI
              { value: 'FreeMargin', label: t`Margem Livre` },
              // eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos do fio (U02), não texto de UI
              { value: 'Portability', label: t`Portabilidade` },
            ]}
          />
          {zetra ? <DateFormField<Values> name="warrantyDeadline__civil" label={t`Data limite de averbação`} disabled={kovr} /> : null}
          {option === 'Portability' ? <TextFormField<Values> name="previousAdeNumber" label={t`Nº ADE anterior`} required /> : null}
          {siape ? <TextFormField<Values> name="agreementCode" label={t`Código convênio`} required /> : null}
          <TextFormField<Values> name="governmentDepartmentCode" label={t`Código órgão`} required />
          <TextFormField<Values> name="employeeCode" label={t`Matrícula`} required />
          {siape ? (
            <>
              <TextFormField<Values> name="employeerInstCode" label={t`Órgão + matrícula do instituidor`} />
              {status ? <TextFormField<Values> name="employmentStatusCode" label={t`Código situação vínculo funcional`} disabled /> : null}
              <EmploymentStatusField />
            </>
          ) : null}
          {facts.manualWarranty ? <TextFormField<Values> name="warrantyCode" label={t`Código de averbação`} /> : null}
          <TextFormField<Values> name="payingSourceName" label={t`Nome da fonte pagadora`} />
          <DocumentFormField<Values> name="payingSourceRegistrationNumber" label={t`CNPJ da fonte pagadora`} legalPerson />
          <MoneyFormField<Values> name="paymentAmountInCents" label={t`Valor da Margem`} required />
        </div>
      ) : null}

      {type === 'InvoiceFactoring' ? (
        <div className={GRID}>
          <TextFormField<Values> name="payerName" label={t`Nome do pagador`} required />
          <DocumentFormField<Values> name="payerRegistrationNumber" label={t`CPF/CNPJ`} />
          <TextFormField<Values> name="contractNumber" label={t`Número do contrato`} required />
          <TextFormField<Values> name="invoiceNumber" label={t`Número nominal`} required />
          <MoneyFormField<Values> name="invoiceValue" label={t`Valor nominal`} required />
          <MoneyFormField<Values> name="discountValue" label={t`Desconto`} required />
          <MoneyFormField<Values> name="presentValue" label={t`Valor presente`} required />
          <MoneyFormField<Values> name="paymentAmountInCents" label={t`Valor nominal no vencimento`} required />
          <DateFormField<Values> name="dueDate__civil" label={t`Data de vencimento`} required />
          <TextFormField<Values> name="paymentItemIndex" label={t`Número da parcela`} inputMode="numeric" />
          <TextFormField<Values> name="paymentItemTotal" label={t`Total de parcelas`} inputMode="numeric" />
          <TextFormField<Values> name="yourNumber" label={t`Seu número`} inputMode="numeric" />
          <TextFormField<Values> name="storeCode" label={t`Código da loja`} inputMode="numeric" />
          <TextFormField<Values> name="productCode" label={t`Código do produto`} inputMode="numeric" />
          <TextFormField<Values> name="identification" label={t`Identificação`} />
          <DateFormField<Values> name="emissionDate__civil" label={t`Data de emissão do título`} />
          <TextFormField<Values> name="index" label={t`Índice`} />
          <TextFormField<Values> name="payerAdress" label={t`Endereço do pagador`} />
          <TextFormField<Values> name="payerZipCodeAdress" label={t`CEP do pagador`} inputMode="numeric" />
          {/* O legado liga o campo a `ContractFee` (maiúscula), que o `toDataModel` não leva ao fio (WarrantyForm.tsx:1134-1140). */}
          <PercentFormField<Values> name="ContractFee" label={t`Taxa do contrato`} />
        </div>
      ) : null}

      {type === 'Consortium' ? (
        <div className={GRID}>
          <DocumentFormField<Values> name="administratorRegistrationNumber" label={t`CNPJ do Administrador`} legalPerson />
          <TextFormField<Values> name="administratorName" label={t`Nome do Administrador`} />
          <TextFormField<Values> name="group" label={t`Grupo`} required />
          <TextFormField<Values> name="quota" label={t`Cota`} required />
        </div>
      ) : null}

      {type === 'Load' ? (
        <div className={GRID}>
          <TextFormField<Values> name="idInvoice" label={t`Número da fatura`} />
          <TextFormField<Values> name="blNumber" label={t`Número de Conhecimento de Embarque`} />
          <TextFormField<Values> name="ncm" label={t`NCM`} />
          <TextFormField<Values> name="valueBRL" label={t`Valor BRL`} />
          <TextFormField<Values> name="policyNumber" label={t`Número da Apólice`} />
          <TextFormField<Values> name="insuranceCompanyName" label={t`Nome da Seguradora`} />
          <DocumentFormField<Values> name="insuranceCompanyCNPJ" label={t`CNPJ da seguradora`} legalPerson />
        </div>
      ) : null}

      {kovrWarning ? (
        <Alert tone="warn">{t`A data limite de averbação será atualizada para hoje + 30 dias ao salvar.`}</Alert>
      ) : null}
    </>
  );
}

/** "Margem disponível": o legado copia o valor para "Margem averbação" (WarrantyForm.tsx:292-300). */
function InssMarginField() {
  const { t } = useLingui();
  const { control, setValue } = useFormContext<Values>();
  const value = useWatch({ control, name: 'inss_AvailableMargin' });
  useEffect(() => {
    if (typeof value === 'number') setValue('inss_MarginValue', value, { shouldDirty: true });
  }, [value, setValue]);
  return <MoneyFormField<Values> name="inss_AvailableMargin" label={t`Margem disponível`} required />;
}

/** CEP do imóvel: com 8 dígitos, preenche endereço, bairro, cidade e UF (`queryZipCodeAndFillForm`). */
function ZipField({ onComplete }: { readonly onComplete: (zip: string) => void }) {
  const { t } = useLingui();
  const { control } = useFormContext<Values>();
  const zip = useWatch({ control, name: 'zipCodeAddress' });
  useEffect(() => {
    if (typeof zip === 'string' && zip.length === 8) onComplete(zip);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só quando o CEP muda (U02: o legado consulta na digitação)
  }, [zip]);
  return <MaskedFormField<Values> name="zipCodeAddress" label={t`CEP`} kind="cep" required />;
}

// ─── o drawer ───────────────────────────────────────────────────────────────

export interface WarrantySheetProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** A garantia em edição; ausente, "Adicionar garantia". */
  readonly item: WarrantyItem | undefined;
  readonly facts: WarrantyProductFacts;
  readonly kovr: boolean;
  readonly alphanumeric: boolean;
  readonly borrower: { readonly name: string | null; readonly document: string | null };
  readonly onSubmit: (item: WarrantyItem) => void;
}

export function WarrantySheet({ open, onOpenChange, item, facts, kovr, alphanumeric, borrower, onSubmit }: WarrantySheetProps) {
  const { i18n, t } = useLingui();
  const [now] = useState(() => new Date());
  const edit = item !== undefined;
  const defaults = item ? warrantyFormValues(item) : withCivil(defaultWarrantyValues(now));
  const autofilledInssCpf = borrower.document ? borrower.document.replace(/[^A-Za-z0-9]/g, '') : null;
  return (
    <EntitySheet<Values>
      open={open}
      onOpenChange={onOpenChange}
      title={t`Adicionar/Alterar Garantia`}
      description={t`Confira as informações da garantia. Ela só é gravada quando você salvar a operação.`}
      schema={warrantySchema(i18n, facts)}
      defaultValues={defaults}
      submitLabel={edit ? t`Salvar garantia` : t`Adicionar`}
      cancelLabel={t`Fechar`}
      mobile="screen"
      onSubmit={(values) => onSubmit(warrantyItemFromForm(values, { facts, edit, original: item, kovr, now, autofilledInssCpf, alphanumeric }))}
    >
      {(form) => <WarrantyFields form={form} facts={facts} options={warrantyTypeOptions(facts)} edit={edit} kovr={kovr} now={now} borrower={borrower} />}
    </EntitySheet>
  );
}

function withCivil(values: Values): Values {
  const out: Values = { ...values };
  for (const field of DATE_FIELDS) {
    const civil = civilOf(values[field]);
    if (civil) out[`${field}__civil`] = civil;
  }
  return out;
}
